import { useEffect, useState, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { Paper, Container as MuiContainer, CssBaseline } from "@mui/material";

import GlobalStyle from "@/components/GlobalStyle";
import Navbar from "@/components/navbar/Navbar";
import sideBarItems, {
  GPC_PATH,
  GPRT_PATH,
} from "@/components/sidebar/sideBarItems";
import Sidebar from "@/components/sidebar/Sidebar";
import Footer from "@/components/Footer";
import { NotificationsProvider } from "@/contexts/NotificationsProvider";
import { useGpcPendingRequestsCount } from "@/hooks/useGpcPendingRequestsCount";
import { useGprtAnsweredRequestsCount } from "@/hooks/useGprtAnsweredRequestsCount";

const drawerWidth = 258;

// DOM id of `Container` below — everything right of the sidebar, below the
// navbar, above the footer. A `Sheet` can pass this as `boundedContainerId`
// to scope itself to that region instead of the full viewport.
export const APP_MAIN_CONTENT_ID = "app-main-content";

const Root = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 0;
  overflow: hidden;
`;

const MainContent = styled(Paper)`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const Container = styled(MuiContainer)`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: 0;
  margin: 0;
  max-width: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${(props) => props.theme.palette.background.default};
`;

const PricingAuditLayoutInner = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const gpcPendingCount = useGpcPendingRequestsCount();
  const gprtAnsweredCount = useGprtAnsweredRequestsCount();
  const location = useLocation();

  // The layout persists across navigation (so sidebar collapse state survives
  // tab switches), so the mobile drawer needs an explicit close on route
  // change instead of relying on a remount to reset it.
  useEffect(() => {
    setMobileOpen(false);
    // location.key changes on every navigation (including query/hash-only
    // changes on the same path), unlike location.pathname which would miss those.
  }, [location.key]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleToggleCollapse = () => setCollapsed(!collapsed);

  const itemsWithBadge = useMemo(
    () =>
      sideBarItems.map((section) => ({
        ...section,
        pages: section.pages.map((page) => {
          if (page.href === GPC_PATH && gpcPendingCount > 0) {
            return { ...page, badge: String(gpcPendingCount) };
          }
          if (page.href === GPRT_PATH && gprtAnsweredCount > 0) {
            return { ...page, badge: String(gprtAnsweredCount) };
          }
          return page;
        }),
      })),
    [gpcPendingCount, gprtAnsweredCount]
  );

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      {/* Mobile Sidebar — renders via a Radix Dialog portal, invisible until opened */}
      <Sidebar
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        items={itemsWithBadge}
        width={drawerWidth}
      />
      {/* Desktop Sidebar */}
      <div className="hidden shrink-0 md:block">
        <Sidebar
          variant="permanent"
          items={itemsWithBadge}
          collapsed={collapsed}
          onToggleCollapse={handleToggleCollapse}
          width={drawerWidth}
        />
      </div>
      <AppContent>
        <Navbar onDrawerToggle={handleDrawerToggle} />
        <Container disableGutters id={APP_MAIN_CONTENT_ID}>
          <MainContent>
            <Outlet />
          </MainContent>
        </Container>
        <Footer />
      </AppContent>
    </Root>
  );
};

const PricingAuditLayout = () => (
  <NotificationsProvider>
    <PricingAuditLayoutInner />
  </NotificationsProvider>
);

export default PricingAuditLayout;
